/**
 * UI State Store
 *
 * 管理 UI 狀態（抽屜、模態框、視窗尺寸等）
 */

import { defineStore } from 'pinia'
import { markRaw, type Component } from 'vue'
import equipmentService from '@shared/services/equipment'
import { eventBus } from '@shared/stores/globalState'
import { formatDate } from '@shared/utils/dateUtils'
import type { App } from 'vue'
import {
  type DrawerSlot,
  type DrawerStackItem,
  type DrawerType,
  BASE_Z_INDEX,
  MAX_DRAWERS,
} from '@shared/types/stores/uiState'

// ==================== 類型定義 ====================

/** 抽屜參數 */
export interface DrawerParameter {
  equipmentId?: string
  fieldId?: string
  [key: string]: unknown
}

// 重新匯出類型供外部使用
export type { DrawerSlot, DrawerStackItem, DrawerType }

/** 模態框參數 */
export interface ModalParameter {
  [key: string]: unknown
}

/** OpenView 選項 */
export interface OpenViewOptions {
  width?: number
  height?: number
  [key: string]: unknown
}

/** 設備資料（簡化） */
export interface EquipmentData {
  id: string
  title?: string
  updated_at?: string | Date
  [key: string]: unknown
}

/** UI State 介面 */
export interface UIStateType {
  isMobile: boolean
  isTablet: boolean
  windowWidth: number
  windowHeight: number
  // Drawer (原有單一 Drawer - 向後相容)
  showDrawer: boolean
  drawerName: string | Component
  drawerTitle: string
  drawerWidth: number
  drawerParameter: DrawerParameter
  drawerFullBody: boolean
  currentDrawer: Component | null
  // 多層級 Drawer 槽位
  drawerSlots: DrawerSlot[]
  // 全局 z-index 堆疊追蹤
  drawerStack: DrawerStackItem[]
  nextZIndex: number
  // Modal
  showModal: boolean
  modalName: string
  modalTitle: string
  modalParameter: ModalParameter
  // Preview
  showPreview: boolean
  previewSrcList: string[]
  lastPopupName: string
  // Page
  currentPage: string | null
  pageTitle: string
  menuTitle: string
  landingPage: string
}

// ==================== Store 定義 ====================

export const uiState = defineStore('uiState', {
  state: (): UIStateType => ({
    isMobile: false,
    isTablet: false,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    // Drawer (原有單一 Drawer - 向後相容)
    showDrawer: false,
    drawerName: '',
    drawerTitle: '',
    drawerWidth: 600,
    drawerParameter: {},
    drawerFullBody: false,
    currentDrawer: null,
    // 多層級 Drawer 槽位
    drawerSlots: [
      { id: 1, show: false, name: '', title: '', parameter: {}, fullBody: false, zIndex: 0 },
      { id: 2, show: false, name: '', title: '', parameter: {}, fullBody: false, zIndex: 0 },
      { id: 3, show: false, name: '', title: '', parameter: {}, fullBody: false, zIndex: 0 },
      { id: 4, show: false, name: '', title: '', parameter: {}, fullBody: false, zIndex: 0 },
    ],
    // 全局 z-index 堆疊追蹤
    drawerStack: [],
    nextZIndex: BASE_Z_INDEX,
    // Modal
    showModal: false,
    modalName: '',
    modalTitle: '',
    modalParameter: {},
    // Preview
    showPreview: false,
    previewSrcList: [],
    lastPopupName: '',
    // Page
    currentPage: null,
    pageTitle: '',
    menuTitle: '',
    landingPage: '/patrol/WorkFlows',
  }),

  actions: {
    /**
     * 處理視窗大小變化
     */
    handleWindowResize(): void {
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight
      this.drawerWidth = window.innerWidth * 0.86
      if (window.innerWidth >= 969) this.drawerWidth = window.innerWidth * 0.6
      if (window.innerWidth >= 1500) this.drawerWidth = window.innerWidth * 0.4
      this.isTablet = this.windowWidth <= 1440
      this.isMobile = this.windowWidth <= 770
    },

    /**
     * 開啟模態框
     */
    openModal(modalName: string, modalTitle: string = '', modalParameter: ModalParameter = {}): void {
      this.showModal = true
      this.modalTitle = modalTitle
      this.modalParameter = modalParameter
      setTimeout(() => {
        this.modalName = modalName
      }, 330)
    },

    /**
     * 開啟視窗（透過 eventBus）
     */
    openView(dir: string, page: string, title: string, options: OpenViewOptions = {}): void {
      const path = `/${dir}/${page}`
      eventBus.emit('view:open', { path, title, options })
    },

    /**
     * 開啟抽屜 (向後相容 - 會關閉所有現有 drawer 後開啟新的)
     *
     * 同時透過 eventBus 發送事件，確保在 Module Federation 模式下
     * 即使 Pinia store 沒有正確共享也能運作
     */
    openDrawer(
      drawerName: string | Component,
      drawerTitle: string = '',
      drawerParameter: DrawerParameter = {},
      fullBody: boolean = false
    ): void {
      // 關閉所有現有 drawer 以維持向後相容行為
      this.closeAllDrawers()
      // 使用堆疊模式開啟新 drawer
      this.openDrawerStacked(drawerName, drawerTitle, drawerParameter, fullBody)

      // 同時發送 eventBus 事件（供 Module Federation 模式使用）
      // 當 Remote 的 Pinia store 與 Host 不同步時，Host 會監聽此事件
      if (typeof drawerName === 'string') {
        eventBus.emit('ui:open-drawer', {
          name: drawerName,
          title: drawerTitle,
          params: drawerParameter,
          fullBody
        })
      }
    },

    /**
     * 開啟抽屜（使用組件物件）(向後相容)
     */
    openDrawerWithObject(
      drawerNameObject: Component,
      drawerTitle: string = '',
      drawerParameter: DrawerParameter = {},
      fullBody: boolean = false
    ): void {
      this.openDrawer(markRaw(drawerNameObject), drawerTitle, drawerParameter, fullBody)
    },

    /**
     * 開啟設備詳情抽屜
     */
    openDrawerWithEquipment(equipment: EquipmentData): void {
      try {
        equipmentService.removeAllListeners()
      } catch (error) {
        console.warn('[uiState] 停止 Firebase 監聽器時發生錯誤:', error)
      }
      this.openDrawer('drawer_EquipmentDetail', (equipment.title || '') + ' 詳細資訊', { equipmentId: equipment.id })
    },

    /**
     * 關閉抽屜 (向後相容 - 關閉所有 drawer)
     */
    closeDrawer(): void {
      this.closeAllDrawers()
    },

    /**
     * 關閉模態框
     */
    closeModal(): void {
      this.showModal = false
      this.modalName = ''
    },

    /**
     * 取得最後更新時間
     */
    lastUpdateTime(data: EquipmentData | null | undefined): string {
      if (!data?.updated_at) return '資料尚未更新'
      return formatDate(data.updated_at)
    },

    // ==================== z-index 管理 ====================

    /**
     * 獲取下一個 z-index (任何 drawer 開啟時呼叫)
     */
    getNextZIndex(type: DrawerType, id: number | string): number {
      const zIndex = this.nextZIndex
      this.nextZIndex += 10
      this.drawerStack.push({ type, id, zIndex })
      return zIndex
    },

    /**
     * 釋放 z-index (任何 drawer 關閉時呼叫)
     */
    releaseZIndex(type: DrawerType, id: number | string): void {
      const index = this.drawerStack.findIndex(
        item => item.type === type && item.id === id
      )
      if (index > -1) {
        this.drawerStack.splice(index, 1)
      }
      // 如果堆疊清空，重置計數器
      if (this.drawerStack.length === 0) {
        this.nextZIndex = BASE_Z_INDEX
      }
    },

    // ==================== 多層級 Drawer 堆疊管理 ====================

    /**
     * 開啟 Drawer (堆疊模式)
     * @returns 槽位 ID，若無可用槽位返回 -1
     */
    openDrawerStacked(
      drawerName: string | Component,
      drawerTitle: string = '',
      drawerParameter: DrawerParameter = {},
      fullBody: boolean = false
    ): number {
      const slot = this.drawerSlots.find(s => !s.show)
      if (!slot) {
        console.warn('[uiState] 已達最大 Drawer 數量限制')
        return -1
      }

      slot.show = true
      slot.title = drawerTitle
      slot.parameter = drawerParameter
      slot.fullBody = fullBody
      slot.zIndex = this.getNextZIndex('slot', slot.id)

      setTimeout(() => {
        slot.name = typeof drawerName === 'object'
          ? markRaw(drawerName as Component)
          : drawerName
      }, 330)

      return slot.id
    },

    /**
     * 關閉指定槽位的 Drawer
     */
    closeDrawerById(id: number): void {
      const slot = this.drawerSlots.find(s => s.id === id)
      if (slot) {
        this.releaseZIndex('slot', slot.id)
        slot.show = false
        slot.name = ''
        slot.title = ''
        slot.parameter = {}
        slot.fullBody = false
        slot.zIndex = 0
      }
    },

    /**
     * 關閉最上層的 Drawer
     */
    closeTopDrawer(): void {
      if (this.drawerStack.length === 0) return

      const top = this.drawerStack[this.drawerStack.length - 1]
      if (top.type === 'slot') {
        this.closeDrawerById(top.id as number)
      }
      // 其他類型由各自組件處理
    },

    /**
     * 關閉所有 Drawer 槽位
     */
    closeAllDrawers(): void {
      this.drawerSlots.forEach(slot => {
        if (slot.show) {
          this.releaseZIndex('slot', slot.id)
        }
        slot.show = false
        slot.name = ''
        slot.title = ''
        slot.parameter = {}
        slot.fullBody = false
        slot.zIndex = 0
      })
    },
  },

  // ==================== Getters ====================

  getters: {
    /** 任一 Drawer 槽位是否開啟 */
    hasOpenDrawer: (state) => state.drawerSlots.some(s => s.show),

    /** 開啟中的 Drawer 槽位數量 */
    openDrawerCount: (state) => state.drawerSlots.filter(s => s.show).length,

    /** 全局是否有任何 drawer 開啟 */
    hasAnyDrawerOpen: (state) => state.drawerStack.length > 0,

    /** 最上層的 drawer 資訊 */
    topDrawer: (state): DrawerStackItem | null => {
      if (state.drawerStack.length === 0) return null
      return state.drawerStack[state.drawerStack.length - 1]
    },
  },
})

// ==================== Vue Plugin ====================

export default {
  install: (app: App): void => {
    const ui = uiState()
    app.config.globalProperties.ui = ui
  }
}
